import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Space, Tag, Modal, Switch, InputNumber,
  Select, message, Tooltip, Input, Spin, Progress, Typography, Row, Col, Statistic,
  Alert, Divider,
} from 'antd';
import {
  CloudDownloadOutlined, DeleteOutlined, UndoOutlined,
  PlusOutlined, ScheduleOutlined, DatabaseOutlined, CodeOutlined,
  ClockCircleOutlined, ExclamationCircleOutlined, CheckCircleOutlined,
  ReloadOutlined, SettingOutlined, SaveOutlined, InfoCircleOutlined,
  CloudServerOutlined, HddOutlined, CloudUploadOutlined, ThunderboltOutlined,
  RobotOutlined, ApiOutlined, CheckOutlined, CloseOutlined, SendOutlined,
  DeploymentUnitOutlined, LinkOutlined, GlobalOutlined, PlayCircleOutlined,
} from '@ant-design/icons';
import { backupAPI } from '../services/api';

const { Title, Text } = Typography;
const { confirm } = Modal;

export default function BackupManagementPage() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [schedule, setSchedule] = useState({
    enabled: false, interval_hours: 24, include_db: true, include_code: true,
    include_claude: false, max_keep: 7, auto_push: false, auto_push_gitlab: false,
  });
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createOpts, setCreateOpts] = useState({ include_db: true, include_code: true, include_claude: false, note: '' });
  const [disk, setDisk] = useState(null);

  // Remote restore state
  const [remoteModalOpen, setRemoteModalOpen] = useState(false);
  const [remoteTarget, setRemoteTarget] = useState(null); // backup record being restored
  const [remoteRunning, setRemoteRunning] = useState(false);
  const [remoteResult, setRemoteResult] = useState(null);
  const [remoteCfg, setRemoteCfg] = useState({
    target_ip: '', ssh_user: 'root', ssh_port: 22,
    key_path: '/root/.ssh/id_rsa',
    target_dir: '/root/docker/spo',
    pg_container: 'spo-postgres', pg_user: 'spo_user', pg_db: 'spo_db',
    restore_db: true, restore_code: true, restart_docker: true,
    app_port: 3000, protocol: 'http',
  });

  // SSH Push state
  const [ssh, setSsh] = useState({
    host: '', user: 'root', port: 22, remote_dir: '/backups', key_path: '/root/.ssh/id_rsa',
    tmux_enabled: false, tmux_session: '', tmux_pane: '', tmux_cmd: '',
  });
  const [sshLoading, setSshLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [pushing, setPushing] = useState(null); // filename being pushed or 'latest' or 'tmux'

  // GitLab Push state
  const [gitlab, setGitlab] = useState({
    enabled: false, host: 'http://10.66.88.30', token: '',
    project_path: 'jeffdev/spo-backups', branch: 'main', auto_push: false,
  });
  const [gitlabLoading, setGitlabLoading] = useState(false);
  const [gitlabPushing, setGitlabPushing] = useState(null);
  const [gitlabLog, setGitlabLog] = useState([]);

  const fetchBackups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await backupAPI.list();
      const d = res.data || res;
      setBackups(d.backups || []);
      if (d.disk) setDisk(d.disk);
    } catch (e) {
      message.error('Failed to load backups');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSchedule = useCallback(async () => {
    try {
      const res = await backupAPI.getSchedule();
      setSchedule(res.data || res);
    } catch { /* ignore */ }
  }, []);

  const fetchSSH = useCallback(async () => {
    try {
      const res = await backupAPI.getSSHConfig();
      setSsh(res.data || res);
    } catch { /* ignore */ }
  }, []);

  const fetchGitLab = useCallback(async () => {
    try {
      const [cfgRes, logRes] = await Promise.all([
        backupAPI.getGitLabConfig(),
        backupAPI.getGitLabLog(),
      ]);
      setGitlab(cfgRes.data || cfgRes);
      setGitlabLog(Array.isArray(logRes.data) ? logRes.data : (Array.isArray(logRes) ? logRes : []));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchBackups();
    fetchSchedule();
    fetchSSH();
    fetchGitLab();
  }, [fetchBackups, fetchSchedule, fetchSSH, fetchGitLab]);

  const handleSSHSave = async () => {
    setSshLoading(true);
    try {
      await backupAPI.saveSSHConfig(ssh);
      message.success('SSH 設定已儲存');
    } catch (e) {
      message.error('儲存失敗');
    } finally {
      setSshLoading(false);
    }
  };

  const handleSSHTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await backupAPI.testSSH();
      setTestResult(res.data || res);
    } catch (e) {
      setTestResult({ ok: false, error: e.message || '連線失敗' });
    } finally {
      setTesting(false);
    }
  };

  const handleGitLabSave = async () => {
    setGitlabLoading(true);
    try {
      await backupAPI.saveGitLabConfig(gitlab);
      message.success('GitLab 設定已儲存');
    } catch { message.error('儲存失敗'); }
    finally { setGitlabLoading(false); }
  };

  const handleGitLabPush = async (filename) => {
    setGitlabPushing(filename || 'latest');
    try {
      const res = await backupAPI.pushToGitLab(filename);
      const d = res.data || res;
      if (d.ok) message.success(`✅ GitLab push 成功：${d.project}`);
      else message.error(`GitLab push 失敗：${d.error || '未知錯誤'}`);
    } catch (e) {
      message.error('GitLab push 失敗：' + e.message);
    } finally {
      setGitlabPushing(null);
    }
  };

  const handlePush = async (filename) => {
    setPushing(filename || 'latest');
    try {
      const res = await backupAPI.pushBackup(filename);
      const d = res.data || res;
      if (d.ok) {
        message.success(`推送成功：${d.remote_path}${d.tmux ? ' + tmux 指令已送出' : ''}`);
      } else {
        message.error(`推送失敗：${d.error}`);
      }
    } catch (e) {
      message.error('推送失敗');
    } finally {
      setPushing(null);
    }
  };

  const handleTmuxPush = async () => {
    setPushing('tmux');
    try {
      const res = await backupAPI.tmuxPush();
      const d = res.data || res;
      if (d.ok) {
        message.success(`tmux 指令已送出至 ${d.session}`);
      } else {
        message.error(`送出失敗：${d.error}`);
      }
    } catch (e) {
      message.error('送出失敗');
    } finally {
      setPushing(null);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    setCreateModalOpen(false);
    try {
      const res = await backupAPI.create(createOpts);
      const d = res.data || res;
      message.success(`備份建立成功：${d.filename}（${d.size_display}）`);
      fetchBackups();
    } catch (e) {
      message.error(`備份失敗：${e.message || 'Unknown error'}`);
    } finally {
      setCreating(false);
      setCreateOpts({ include_db: true, include_code: true, include_claude: false, note: '' });
    }
  };

  const handleRestore = (record) => {
    confirm({
      title: '確認還原資料庫',
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      content: (
        <div>
          <p>即將從以下備份還原資料庫：</p>
          <p><strong>{record.filename}</strong></p>
          <p style={{ color: '#ff4d4f' }}>⚠️ 此操作將覆蓋目前資料庫，建議先建立新備份！</p>
        </div>
      ),
      okText: '確認還原',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        setRestoring(record.filename);
        try {
          const res = await backupAPI.restore(record.filename);
          const rd = res.data || res;
          if (rd.success) {
            message.success('資料庫還原成功！');
          } else {
            message.warning(rd.message);
          }
        } catch (e) {
          message.error(`還原失敗：${e.message || 'Unknown error'}`);
        } finally {
          setRestoring(null);
        }
      },
    });
  };

  const handleDelete = (record) => {
    confirm({
      title: '確認刪除備份',
      icon: <ExclamationCircleOutlined />,
      content: `確定要刪除 ${record.filename}？此操作無法復原。`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await backupAPI.remove(record.filename);
          message.success('備份已刪除');
          fetchBackups();
        } catch (e) {
          message.error(`刪除失敗：${e.message}`);
        }
      },
    });
  };

  const openRemoteRestore = (record) => {
    setRemoteTarget(record);
    setRemoteResult(null);
    setRemoteModalOpen(true);
  };

  const handleRemoteRestore = async () => {
    if (!remoteCfg.target_ip.trim()) { message.warning('請輸入目標主機 IP'); return; }
    setRemoteRunning(true);
    setRemoteResult(null);
    try {
      // Start async job — returns immediately with job_id
      const res = await backupAPI.restoreRemote({ filename: remoteTarget.filename, ...remoteCfg });
      const d = res.data || res;
      if (!d.job_id) throw new Error('未收到 job_id');

      // Show terminal immediately with empty logs
      setRemoteResult({ _polling: true, logs: ['[等待伺服器回應...]'], ok: null });

      // Poll for live logs every 2 seconds
      const poll = setInterval(async () => {
        try {
          const sr = await backupAPI.restoreRemoteStatus(d.job_id);
          const s = sr.data || sr;
          setRemoteResult(prev => ({ ...prev, logs: s.logs || prev.logs }));
          if (s.status === 'done' || s.status === 'error') {
            clearInterval(poll);
            setRemoteRunning(false);
            setRemoteResult(s.status === 'done'
              ? { ...s, ok: s.ok !== false }
              : { ok: false, error: s.error || '還原失敗', logs: s.logs || [] });
            if (s.status === 'done' && s.ok !== false) {
              message.success('異機還原完成！');
            } else {
              message.error('還原失敗');
            }
          }
        } catch (_) { /* keep polling */ }
      }, 2000);
    } catch (e) {
      setRemoteResult({ ok: false, error: e.message || '發生錯誤', logs: [] });
      setRemoteRunning(false);
      message.error('還原啟動失敗');
    }
  };

  const handleScheduleSave = async () => {
    setScheduleLoading(true);
    try {
      await backupAPI.updateSchedule(schedule);
      message.success('排程設定已儲存');
    } catch (e) {
      message.error('儲存排程失敗');
    } finally {
      setScheduleLoading(false);
    }
  };

  const totalSize = backups.reduce((sum, b) => sum + (b.size_bytes || 0), 0);
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const columns = [
    {
      title: '備份檔案',
      dataIndex: 'filename',
      key: 'filename',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>
            <HddOutlined style={{ marginRight: 6, color: '#1890ff' }} />
            {text}
          </div>
          <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>
            /root/docker/spo/backups/{text}
          </Text>
          {record.note && <div><Text type="secondary" style={{ fontSize: 12 }}>{record.note}</Text></div>}
        </div>
      ),
    },
    {
      title: '類型',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type) => {
        const config = {
          full: { color: 'blue', label: '完整備份' },
          db_only: { color: 'green', label: '僅資料庫' },
          code_only: { color: 'orange', label: '僅程式碼' },
        };
        const c = config[type] || config.full;
        return <Tag color={c.color}>{c.label}</Tag>;
      },
    },
    {
      title: '內容',
      key: 'includes',
      width: 140,
      render: (_, record) => (
        <Space size={4}>
          {record.includes_db && (
            <Tooltip title="包含資料庫">
              <Tag icon={<DatabaseOutlined />} color="processing">DB</Tag>
            </Tooltip>
          )}
          {record.includes_code && (
            <Tooltip title="包含程式碼">
              <Tag icon={<CodeOutlined />} color="success">Code</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: '大小',
      dataIndex: 'size_display',
      key: 'size_display',
      width: 100,
      render: (text) => <span style={{ fontFamily: 'monospace' }}>{text}</span>,
    },
    {
      title: '建立時間',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text) => {
        if (!text) return '-';
        const d = new Date(text);
        return (
          <Tooltip title={d.toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' })}>
            <ClockCircleOutlined style={{ marginRight: 4 }} />
            {d.toLocaleString('zh-TW', {
              timeZone: 'Asia/Taipei', month: '2-digit', day: '2-digit',
              hour: '2-digit', minute: '2-digit',
            })}
          </Tooltip>
        );
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 240,
      render: (_, record) => (
        <Space>
          <Tooltip title="還原資料庫">
            <Button
              type="primary" ghost size="small" icon={<UndoOutlined />}
              loading={restoring === record.filename}
              disabled={!record.includes_db}
              onClick={() => handleRestore(record)}
            >還原</Button>
          </Tooltip>
          <Tooltip title="SSH 推送到遠端">
            <Button
              size="small" icon={<CloudUploadOutlined />}
              loading={pushing === record.filename}
              disabled={!ssh.host}
              onClick={() => handlePush(record.filename)}
            >推送</Button>
          </Tooltip>
          <Tooltip title="推送到 GitLab">
            <Button
              size="small" icon={<span>🦊</span>}
              loading={gitlabPushing === record.filename}
              disabled={!gitlab.host || !gitlab.token}
              style={{ borderColor: '#e24329', color: '#e24329' }}
              onClick={() => handleGitLabPush(record.filename)}
            >GitLab</Button>
          </Tooltip>
          <Tooltip title="還原至異機（SSH）">
            <Button
              size="small" icon={<DeploymentUnitOutlined />}
              style={{ borderColor: '#722ed1', color: '#722ed1' }}
              onClick={() => openRemoteRestore(record)}
            >異機還原</Button>
          </Tooltip>
          <Tooltip title="刪除備份">
            <Button danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ marginBottom: 4 }}>
          <CloudServerOutlined style={{ marginRight: 8 }} />
          系統備份管理
        </Title>
        <Text type="secondary">管理系統備份、設定自動排程、執行資料庫還原</Text>
      </div>

      {/* Disk Info */}
      {disk && (
        <Card size="small" style={{ marginBottom: 16, background: '#f6f8fa' }}>
          <Row align="middle" gutter={24}>
            <Col>
              <Space>
                <HddOutlined style={{ fontSize: 18, color: '#1890ff' }} />
                <div>
                  <Text strong>備份路徑：</Text>
                  <Text code>{disk.path}</Text>
                </div>
              </Space>
            </Col>
            <Col flex="auto">
              <Row gutter={16} align="middle">
                <Col>
                  <Text type="secondary">磁碟空間：</Text>
                </Col>
                <Col flex="auto" style={{ maxWidth: 300 }}>
                  <Progress
                    percent={disk.usage_percent}
                    strokeColor={disk.usage_percent > 90 ? '#ff4d4f' : disk.usage_percent > 70 ? '#faad14' : '#52c41a'}
                    size="small"
                  />
                </Col>
                <Col>
                  <Text style={{ fontSize: 12 }}>
                    已用 <Text strong>{disk.used_display}</Text> / 共 <Text strong>{disk.total_display}</Text>
                    {' · '}剩餘 <Text strong style={{ color: disk.free_bytes < 1024*1024*1024 ? '#ff4d4f' : '#52c41a' }}>{disk.free_display}</Text>
                  </Text>
                </Col>
              </Row>
            </Col>
          </Row>
        </Card>
      )}

      {/* Stats Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="備份總數"
              value={backups.length}
              prefix={<HddOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="備份佔用"
              value={formatSize(totalSize)}
              prefix={<CloudDownloadOutlined />}
              valueStyle={{ color: '#52c41a', fontSize: 20 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="自動備份"
              value={schedule.enabled ? '已啟用' : '未啟用'}
              prefix={<ScheduleOutlined />}
              valueStyle={{ color: schedule.enabled ? '#52c41a' : '#999', fontSize: 20 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="最新備份"
              value={backups[0] ? new Date(backups[0].created_at).toLocaleString('zh-TW', {
                timeZone: 'Asia/Taipei', month: '2-digit', day: '2-digit',
                hour: '2-digit', minute: '2-digit',
              }) : '無'}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ fontSize: 18 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Schedule Card */}
      <Card
        title={<span><ScheduleOutlined style={{ marginRight: 8 }} />自動備份排程</span>}
        size="small"
        style={{ marginBottom: 24 }}
        extra={
          <Button
            type="primary"
            icon={<SaveOutlined />}
            size="small"
            loading={scheduleLoading}
            onClick={handleScheduleSave}
          >
            儲存排程
          </Button>
        }
      >
        <Row gutter={24} align="middle">
          <Col>
            <Space>
              <Text>啟用自動備份：</Text>
              <Switch
                checked={schedule.enabled}
                onChange={(v) => setSchedule(s => ({ ...s, enabled: v }))}
                checkedChildren="ON"
                unCheckedChildren="OFF"
              />
            </Space>
          </Col>
          <Col>
            <Space>
              <Text>備份間隔：</Text>
              <Select
                value={schedule.interval_hours}
                onChange={(v) => setSchedule(s => ({ ...s, interval_hours: v }))}
                style={{ width: 130 }}
                disabled={!schedule.enabled}
                options={[
                  { value: 6, label: '每 6 小時' },
                  { value: 12, label: '每 12 小時' },
                  { value: 24, label: '每天' },
                  { value: 48, label: '每 2 天' },
                  { value: 168, label: '每週' },
                ]}
              />
            </Space>
          </Col>
          <Col>
            <Space>
              <Text>保留份數：</Text>
              <InputNumber
                min={1}
                max={30}
                value={schedule.max_keep}
                onChange={(v) => setSchedule(s => ({ ...s, max_keep: v }))}
                disabled={!schedule.enabled}
                style={{ width: 70 }}
              />
            </Space>
          </Col>
          <Col>
            <Space>
              <Text>備份內容：</Text>
              <Switch
                checked={schedule.include_db}
                onChange={(v) => setSchedule(s => ({ ...s, include_db: v }))}
                checkedChildren={<><DatabaseOutlined /> DB</>}
                unCheckedChildren="DB"
                disabled={!schedule.enabled}
              />
              <Switch
                checked={schedule.include_code}
                onChange={(v) => setSchedule(s => ({ ...s, include_code: v }))}
                checkedChildren={<><CodeOutlined /> Code</>}
                unCheckedChildren="Code"
                disabled={!schedule.enabled}
              />
              <Switch
                checked={schedule.include_claude}
                onChange={(v) => setSchedule(s => ({ ...s, include_claude: v }))}
                checkedChildren={<><RobotOutlined /> Claude</>}
                unCheckedChildren="Claude"
                disabled={!schedule.enabled}
              />
            </Space>
          </Col>
          <Col>
            <Space>
              <Text>備份後自動推送 SSH：</Text>
              <Switch
                checked={schedule.auto_push}
                onChange={(v) => setSchedule(s => ({ ...s, auto_push: v }))}
                checkedChildren={<><CloudUploadOutlined /> SSH</>}
                unCheckedChildren="不推送"
                disabled={!schedule.enabled || !ssh.host}
              />
            </Space>
          </Col>
          <Col>
            <Space>
              <Text>備份後自動推送 GitLab：</Text>
              <Switch
                checked={schedule.auto_push_gitlab}
                onChange={(v) => setSchedule(s => ({ ...s, auto_push_gitlab: v }))}
                checkedChildren={<><span>🦊</span> GitLab</>}
                unCheckedChildren="不推送"
                disabled={!schedule.enabled || !gitlab.token}
              />
            </Space>
          </Col>
        </Row>
      </Card>

      {/* GitLab Push Config */}
      <Card
        title={<span><span style={{ marginRight: 8 }}>🦊</span>GitLab 備份推送設定</span>}
        size="small"
        style={{ marginBottom: 24 }}
        extra={
          <Space>
            <Button
              type="primary" size="small" icon={<SaveOutlined />}
              loading={gitlabLoading} onClick={handleGitLabSave}
            >儲存</Button>
            <Button
              size="small"
              style={{ borderColor: '#e24329', color: '#e24329' }}
              loading={gitlabPushing === 'latest'}
              disabled={!gitlab.host || !gitlab.token}
              onClick={() => handleGitLabPush(null)}
            >推送最新備份</Button>
          </Space>
        }
      >
        <Row gutter={16}>
          <Col span={8}>
            <Text type="secondary" style={{ fontSize: 12 }}>GitLab Host</Text>
            <Input
              value={gitlab.host}
              onChange={e => setGitlab(g => ({ ...g, host: e.target.value }))}
              placeholder="http://10.66.88.30"
              style={{ marginTop: 4 }}
            />
          </Col>
          <Col span={8}>
            <Text type="secondary" style={{ fontSize: 12 }}>Access Token</Text>
            <Input.Password
              value={gitlab.token}
              onChange={e => setGitlab(g => ({ ...g, token: e.target.value }))}
              placeholder="glpat-xxxx..."
              style={{ marginTop: 4 }}
            />
          </Col>
          <Col span={8}>
            <Text type="secondary" style={{ fontSize: 12 }}>Project Path</Text>
            <Input
              value={gitlab.project_path}
              onChange={e => setGitlab(g => ({ ...g, project_path: e.target.value }))}
              placeholder="jeffdev/spo-backups"
              style={{ marginTop: 4 }}
            />
          </Col>
          <Col span={4} style={{ marginTop: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Branch</Text>
            <Input
              value={gitlab.branch}
              onChange={e => setGitlab(g => ({ ...g, branch: e.target.value }))}
              placeholder="main"
              style={{ marginTop: 4 }}
            />
          </Col>
          <Col span={8} style={{ marginTop: 12, display: 'flex', alignItems: 'flex-end' }}>
            <Space>
              <Text>啟用 GitLab Push：</Text>
              <Switch
                checked={gitlab.enabled}
                onChange={v => setGitlab(g => ({ ...g, enabled: v }))}
                checkedChildren="ON" unCheckedChildren="OFF"
              />
            </Space>
          </Col>
        </Row>
      </Card>

      {/* GitLab Push Log */}
      <Card
        title={<span><span style={{ marginRight: 8 }}>🦊</span>GitLab 推送記錄</span>}
        size="small"
        style={{ marginBottom: 24 }}
        extra={
          <Button size="small" icon={<ReloadOutlined />} onClick={fetchGitLab}>重新整理</Button>
        }
      >
        {gitlabLog.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '16px 0', color: '#999', fontSize: 13 }}>
            尚無推送記錄 — 點擊「推送最新備份」或等待排程自動推送
          </div>
        ) : (
          <Table
            dataSource={gitlabLog}
            rowKey={(r, i) => i}
            size="small"
            pagination={{ pageSize: 10, showSizeChanger: false }}
            columns={[
              {
                title: '推送時間',
                dataIndex: 'pushed_at',
                width: 170,
                render: v => v ? new Date(v).toLocaleString('zh-TW', {
                  timeZone: 'Asia/Taipei',
                  year: 'numeric', month: '2-digit', day: '2-digit',
                  hour: '2-digit', minute: '2-digit',
                }) : '-',
              },
              {
                title: '備份檔案',
                dataIndex: 'filename',
                render: (f, r) => (
                  <Space size={4}>
                    <Text style={{ fontSize: 12 }}>{f || '-'}</Text>
                    {r.size_display && <Tag style={{ fontSize: 11 }}>{r.size_display}</Tag>}
                  </Space>
                ),
              },
              {
                title: '觸發方式',
                dataIndex: 'trigger',
                width: 90,
                render: v => (
                  <Tag color={v === 'schedule' ? 'blue' : 'default'}>
                    {v === 'schedule' ? '⏰ 排程' : '👆 手動'}
                  </Tag>
                ),
              },
              {
                title: '狀態',
                dataIndex: 'ok',
                width: 90,
                render: (ok, r) => ok
                  ? <Tag color="success">✅ 成功</Tag>
                  : <Tag color="error" title={r.error}>❌ 失敗</Tag>,
              },
              {
                title: 'GitLab 連結',
                dataIndex: 'package_url',
                render: url => url
                  ? <a href={url} target="_blank" rel="noreferrer" style={{ fontSize: 11 }}>查看 →</a>
                  : '-',
              },
            ]}
          />
        )}
      </Card>

      {/* SSH Push Config */}
      <Card
        title={<span><ApiOutlined style={{ marginRight: 8 }} />SSH 推送設定</span>}
        size="small"
        style={{ marginBottom: 24 }}
        extra={
          <Space>
            <Button
              size="small"
              icon={testing ? <Spin size="small" /> : (testResult?.ok ? <CheckOutlined /> : <ThunderboltOutlined />)}
              loading={testing}
              onClick={handleSSHTest}
              type={testResult?.ok ? 'default' : 'default'}
              danger={testResult && !testResult.ok}
            >
              測試連線
            </Button>
            <Button
              type="primary" icon={<SaveOutlined />} size="small"
              loading={sshLoading} onClick={handleSSHSave}
            >
              儲存設定
            </Button>
          </Space>
        }
      >
        {testResult && (
          <div style={{
            padding: '6px 12px', marginBottom: 12, borderRadius: 6,
            background: testResult.ok ? '#f6ffed' : '#fff2f0',
            border: `1px solid ${testResult.ok ? '#b7eb8f' : '#ffccc7'}`,
            fontSize: 12, fontFamily: 'monospace',
          }}>
            {testResult.ok
              ? <><CheckOutlined style={{ color: '#52c41a', marginRight: 6 }} />連線成功：{testResult.output}</>
              : <><CloseOutlined style={{ color: '#ff4d4f', marginRight: 6 }} />連線失敗：{testResult.error}</>
            }
          </div>
        )}
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ fontSize: 12 }}>主機（Host）</Text>
              <Input
                value={ssh.host} placeholder="192.168.1.100"
                onChange={(e) => setSsh(s => ({ ...s, host: e.target.value }))}
                style={{ marginTop: 4 }}
              />
            </div>
          </Col>
          <Col xs={12} sm={4}>
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ fontSize: 12 }}>使用者（User）</Text>
              <Input
                value={ssh.user} placeholder="root"
                onChange={(e) => setSsh(s => ({ ...s, user: e.target.value }))}
                style={{ marginTop: 4 }}
              />
            </div>
          </Col>
          <Col xs={12} sm={4}>
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ fontSize: 12 }}>Port</Text>
              <InputNumber
                value={ssh.port} min={1} max={65535} style={{ width: '100%', marginTop: 4 }}
                onChange={(v) => setSsh(s => ({ ...s, port: v }))}
              />
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ fontSize: 12 }}>遠端目錄（Remote Dir）</Text>
              <Input
                value={ssh.remote_dir} placeholder="/backups"
                onChange={(e) => setSsh(s => ({ ...s, remote_dir: e.target.value }))}
                style={{ marginTop: 4 }}
              />
            </div>
          </Col>
          <Col xs={24} sm={12}>
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ fontSize: 12 }}>SSH 金鑰路徑（Key Path）</Text>
              <Input
                value={ssh.key_path} placeholder="/root/.ssh/id_rsa"
                onChange={(e) => setSsh(s => ({ ...s, key_path: e.target.value }))}
                style={{ marginTop: 4 }}
              />
            </div>
          </Col>
        </Row>

        {/* tmux settings */}
        <div style={{ borderTop: '1px dashed #e8e8e8', paddingTop: 12, marginTop: 4 }}>
          <Space style={{ marginBottom: 12 }}>
            <Switch
              checked={ssh.tmux_enabled}
              onChange={(v) => setSsh(s => ({ ...s, tmux_enabled: v }))}
              checkedChildren="tmux 指令：ON"
              unCheckedChildren="tmux 指令：OFF"
            />
            <Text type="secondary" style={{ fontSize: 12 }}>推送完成後自動送出 tmux 指令（例如通知 Claude Code 載入新備份）</Text>
          </Space>
          {ssh.tmux_enabled && (
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <div style={{ marginBottom: 12 }}>
                  <Text strong style={{ fontSize: 12 }}>tmux Session 名稱</Text>
                  <Input
                    value={ssh.tmux_session} placeholder="claude"
                    onChange={(e) => setSsh(s => ({ ...s, tmux_session: e.target.value }))}
                    style={{ marginTop: 4 }}
                  />
                </div>
              </Col>
              <Col xs={24} sm={4}>
                <div style={{ marginBottom: 12 }}>
                  <Text strong style={{ fontSize: 12 }}>Pane（選填）</Text>
                  <Input
                    value={ssh.tmux_pane} placeholder="0:0"
                    onChange={(e) => setSsh(s => ({ ...s, tmux_pane: e.target.value }))}
                    style={{ marginTop: 4 }}
                  />
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div style={{ marginBottom: 12 }}>
                  <Text strong style={{ fontSize: 12 }}>送出的指令</Text>
                  <Input
                    value={ssh.tmux_cmd}
                    placeholder="echo 'context synced' | claude -p 'Context updated'"
                    onChange={(e) => setSsh(s => ({ ...s, tmux_cmd: e.target.value }))}
                    style={{ marginTop: 4 }}
                  />
                </div>
              </Col>
            </Row>
          )}
          {ssh.tmux_enabled && ssh.tmux_session && (
            <Button
              icon={<SendOutlined />}
              loading={pushing === 'tmux'}
              onClick={handleTmuxPush}
              disabled={!ssh.host}
              size="small"
            >
              立即送出 tmux 指令
            </Button>
          )}
        </div>

        {/* Push Latest */}
        {ssh.host && (
          <div style={{ borderTop: '1px dashed #e8e8e8', paddingTop: 12, marginTop: 12 }}>
            <Button
              type="primary" icon={<CloudUploadOutlined />}
              loading={pushing === 'latest'}
              onClick={() => handlePush(null)}
            >
              推送最新備份到遠端
            </Button>
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 12 }}>
              將最新備份檔案 SFTP 傳輸至 {ssh.user}@{ssh.host}:{ssh.remote_dir}
            </Text>
          </div>
        )}
      </Card>

      {/* Backup Table */}
      <Card
        title={<span><HddOutlined style={{ marginRight: 8 }} />備份列表</span>}
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchBackups} loading={loading}>
              重新整理
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateModalOpen(true)}
              loading={creating}
            >
              建立備份
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={backups}
          rowKey="filename"
          loading={loading}
          pagination={false}
          locale={{ emptyText: '尚無備份記錄' }}
          size="middle"
        />
      </Card>

      {/* Restore Info */}
      <Card size="small" style={{ marginTop: 16, background: '#f6f8fa' }}>
        <Space>
          <InfoCircleOutlined style={{ color: '#1890ff' }} />
          <Text type="secondary" style={{ fontSize: 12 }}>
            還原操作僅還原資料庫，程式碼需手動從備份壓縮檔中提取。
            備份檔案存放於伺服器 <code>/root/docker/spo/backups/</code> 目錄。
          </Text>
        </Space>
      </Card>

      {/* Create Backup Modal */}
      <Modal
        title={<span><PlusOutlined style={{ marginRight: 8 }} />建立新備份</span>}
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        onOk={handleCreate}
        okText="開始備份"
        cancelText="取消"
        confirmLoading={creating}
      >
        <div style={{ marginBottom: 20 }}>
          <Text type="secondary">選擇要備份的內容，系統將自動打包並儲存。</Text>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Space>
              <DatabaseOutlined style={{ fontSize: 18, color: '#1890ff' }} />
              <div>
                <div style={{ fontWeight: 600 }}>PostgreSQL 資料庫</div>
                <Text type="secondary" style={{ fontSize: 12 }}>所有資料表、使用者資料、設定</Text>
              </div>
            </Space>
            <Switch
              checked={createOpts.include_db}
              onChange={(v) => setCreateOpts(s => ({ ...s, include_db: v }))}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Space>
              <CodeOutlined style={{ fontSize: 18, color: '#52c41a' }} />
              <div>
                <div style={{ fontWeight: 600 }}>程式碼</div>
                <Text type="secondary" style={{ fontSize: 12 }}>Backend、Frontend、Nginx、Docker 設定</Text>
              </div>
            </Space>
            <Switch
              checked={createOpts.include_code}
              onChange={(v) => setCreateOpts(s => ({ ...s, include_code: v }))}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Space>
              <RobotOutlined style={{ fontSize: 18, color: '#722ed1' }} />
              <div>
                <div style={{ fontWeight: 600 }}>Claude 上下文</div>
                <Text type="secondary" style={{ fontSize: 12 }}>/root/.claude/ 對話記錄與 session JSONL 檔案</Text>
              </div>
            </Space>
            <Switch
              checked={createOpts.include_claude}
              onChange={(v) => setCreateOpts(s => ({ ...s, include_claude: v }))}
            />
          </div>
          <div>
            <Text style={{ fontWeight: 600 }}>備註（選填）</Text>
            <Input.TextArea
              value={createOpts.note}
              onChange={(e) => setCreateOpts(s => ({ ...s, note: e.target.value }))}
              placeholder="例如：升級前備份、版本 2.0 發布前"
              rows={2}
              style={{ marginTop: 4 }}
            />
          </div>
        </div>
      </Modal>

      {/* ── Remote Restore Modal ─────────────────────────────────────── */}
      <Modal
        open={remoteModalOpen}
        onCancel={() => { if (!remoteRunning) setRemoteModalOpen(false); }}
        title={
          <Space>
            <DeploymentUnitOutlined style={{ color: '#722ed1' }} />
            <span>還原至異機</span>
            {remoteTarget && <Tag color="purple">{remoteTarget.filename}</Tag>}
          </Space>
        }
        width={680}
        footer={null}
        maskClosable={!remoteRunning}
      >
        {!remoteResult ? (
          <div>
            <Alert
              type="info" showIcon style={{ marginBottom: 16 }}
              message="前提：已對目標主機執行 ssh-copy-id，SSH 免密登入已就緒"
              description="還原流程：SSH 連線 → SFTP 傳輸備份 → 解壓縮 → 還原 DB → 複製程式碼 → 重啟 Docker → 提供連結"
            />

            <Row gutter={12} style={{ marginBottom: 12 }}>
              <Col span={12}>
                <div style={{ marginBottom: 4 }}><Text strong>目標主機 IP <span style={{ color: '#ff4d4f' }}>*</span></Text></div>
                <Input
                  prefix={<GlobalOutlined />}
                  placeholder="例：10.66.99.200"
                  value={remoteCfg.target_ip}
                  onChange={e => setRemoteCfg(c => ({ ...c, target_ip: e.target.value }))}
                />
              </Col>
              <Col span={6}>
                <div style={{ marginBottom: 4 }}><Text strong>SSH 使用者</Text></div>
                <Input value={remoteCfg.ssh_user} onChange={e => setRemoteCfg(c => ({ ...c, ssh_user: e.target.value }))} />
              </Col>
              <Col span={6}>
                <div style={{ marginBottom: 4 }}><Text strong>SSH Port</Text></div>
                <InputNumber min={1} max={65535} value={remoteCfg.ssh_port} onChange={v => setRemoteCfg(c => ({ ...c, ssh_port: v }))} style={{ width: '100%' }} />
              </Col>
            </Row>

            <Row gutter={12} style={{ marginBottom: 12 }}>
              <Col span={24}>
                <div style={{ marginBottom: 4 }}><Text strong>SSH Key 路徑</Text></div>
                <Input value={remoteCfg.key_path} onChange={e => setRemoteCfg(c => ({ ...c, key_path: e.target.value }))} placeholder="/root/.ssh/id_rsa" />
              </Col>
            </Row>

            <Row gutter={12} style={{ marginBottom: 12 }}>
              <Col span={24}>
                <div style={{ marginBottom: 4 }}><Text strong>目標目錄（docker-compose.yml 所在路徑）</Text></div>
                <Input value={remoteCfg.target_dir} onChange={e => setRemoteCfg(c => ({ ...c, target_dir: e.target.value }))} placeholder="/root/docker/spo" />
              </Col>
            </Row>

            <Divider orientation="left" style={{ fontSize: 12 }}>資料庫設定（僅還原 DB 時使用）</Divider>

            <Row gutter={12} style={{ marginBottom: 12 }}>
              <Col span={10}>
                <div style={{ marginBottom: 4 }}><Text strong>PostgreSQL 容器名稱</Text></div>
                <Input value={remoteCfg.pg_container} onChange={e => setRemoteCfg(c => ({ ...c, pg_container: e.target.value }))} />
              </Col>
              <Col span={7}>
                <div style={{ marginBottom: 4 }}><Text strong>DB 使用者</Text></div>
                <Input value={remoteCfg.pg_user} onChange={e => setRemoteCfg(c => ({ ...c, pg_user: e.target.value }))} />
              </Col>
              <Col span={7}>
                <div style={{ marginBottom: 4 }}><Text strong>資料庫名稱</Text></div>
                <Input value={remoteCfg.pg_db} onChange={e => setRemoteCfg(c => ({ ...c, pg_db: e.target.value }))} />
              </Col>
            </Row>

            <Divider orientation="left" style={{ fontSize: 12 }}>還原選項</Divider>

            <Row gutter={24} style={{ marginBottom: 12 }}>
              <Col span={8}>
                <Space>
                  <Switch checked={remoteCfg.restore_db} onChange={v => setRemoteCfg(c => ({ ...c, restore_db: v }))} />
                  <Text><DatabaseOutlined /> 還原資料庫</Text>
                </Space>
              </Col>
              <Col span={8}>
                <Space>
                  <Switch checked={remoteCfg.restore_code} onChange={v => setRemoteCfg(c => ({ ...c, restore_code: v }))} />
                  <Text><CodeOutlined /> 還原程式碼</Text>
                </Space>
              </Col>
              <Col span={8}>
                <Space>
                  <Switch
                    checked={remoteCfg.restart_docker}
                    disabled={!remoteCfg.restore_code && !remoteCfg.restore_db}
                    onChange={v => setRemoteCfg(c => ({ ...c, restart_docker: v }))}
                  />
                  <Text><PlayCircleOutlined /> 重啟服務</Text>
                </Space>
              </Col>
            </Row>

            <Divider orientation="left" style={{ fontSize: 12 }}>平台網址設定（還原後產生可點連結）</Divider>

            <Row gutter={12} style={{ marginBottom: 20 }}>
              <Col span={8}>
                <div style={{ marginBottom: 4 }}><Text strong>協議</Text></div>
                <Select
                  value={remoteCfg.protocol}
                  onChange={v => setRemoteCfg(c => ({ ...c, protocol: v }))}
                  style={{ width: '100%' }}
                  options={[{ value: 'http', label: 'HTTP' }, { value: 'https', label: 'HTTPS' }]}
                />
              </Col>
              <Col span={16}>
                <div style={{ marginBottom: 4 }}><Text strong>前端 Port</Text></div>
                <InputNumber
                  min={1} max={65535}
                  value={remoteCfg.app_port}
                  onChange={v => setRemoteCfg(c => ({ ...c, app_port: v }))}
                  style={{ width: '100%' }}
                  addonBefore={<GlobalOutlined />}
                />
              </Col>
            </Row>

            <div style={{ background: '#f6f8fa', border: '1px solid #e8e8e8', borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>還原完成後將產生連結：</Text>
              <br />
              <Text strong style={{ color: '#1890ff', fontSize: 14 }}>
                {remoteCfg.protocol}://{remoteCfg.target_ip || '<IP>'}:{remoteCfg.app_port}/
              </Text>
            </div>

            <Button
              type="primary"
              size="large"
              icon={<DeploymentUnitOutlined />}
              block
              loading={remoteRunning}
              style={{ background: '#722ed1', borderColor: '#722ed1' }}
              onClick={handleRemoteRestore}
            >
              {remoteRunning ? '還原中，請耐心等候（可能需要 2-5 分鐘）...' : '開始異機還原'}
            </Button>
          </div>
        ) : (
          <div>
            {/* Result display */}
            <div style={{
              background: '#000', borderRadius: 8, padding: '14px 16px',
              fontFamily: 'monospace', fontSize: 12, lineHeight: 1.7,
              maxHeight: 320, overflowY: 'auto', marginBottom: 16,
            }}>
              {(remoteResult.logs || []).map((line, i) => (
                <div key={i} style={{
                  color: line.startsWith('✅') ? '#52c41a'
                    : line.startsWith('❌') ? '#ff4d4f'
                    : line.startsWith('[') ? '#ffe58f'
                    : line.includes('✓') ? '#95de64'
                    : line.includes('⚠') ? '#ffa940'
                    : '#d9d9d9',
                }}>
                  {line}
                </div>
              ))}
            </div>

            {remoteResult.ok ? (
              <div style={{ textAlign: 'center' }}>
                <CheckCircleOutlined style={{ fontSize: 36, color: '#52c41a', marginBottom: 12, display: 'block' }} />
                <div style={{ marginBottom: 16, fontSize: 15, fontWeight: 600 }}>異機還原成功！平台已在目標機器啟動</div>
                <a href={remoteResult.platform_url} target="_blank" rel="noreferrer">
                  <Button
                    type="primary" size="large" icon={<LinkOutlined />}
                    style={{ marginBottom: 8, width: '100%', fontSize: 15 }}
                  >
                    開啟平台：{remoteResult.platform_url}
                  </Button>
                </a>
                <Row gutter={8} style={{ marginTop: 8 }}>
                  <Col span={12}>
                    <Input
                      readOnly value={remoteResult.platform_url}
                      addonBefore={<GlobalOutlined />}
                      style={{ fontSize: 12 }}
                    />
                  </Col>
                  <Col span={12}>
                    <Button
                      block icon={<CloudDownloadOutlined />}
                      onClick={() => {
                        navigator.clipboard.writeText(remoteResult.platform_url);
                        message.success('已複製網址');
                      }}
                    >複製網址</Button>
                  </Col>
                </Row>
              </div>
            ) : (
              <Alert type="error" showIcon message="還原失敗" description={remoteResult.error} />
            )}

            <Divider />
            <Button block onClick={() => { setRemoteResult(null); }}>重新設定再試一次</Button>
          </div>
        )}
      </Modal>

      {/* Creating overlay */}
      {creating && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.45)', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <Card style={{ textAlign: 'center', borderRadius: 16, padding: 24 }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, fontSize: 16, fontWeight: 600 }}>正在建立備份...</div>
            <Text type="secondary">請勿關閉頁面，這可能需要數秒到數分鐘</Text>
          </Card>
        </div>
      )}
    </div>
  );
}
